// rosterViews.tsx — the six Roster sub-tab views, ported from the approved
// prototype (events-live/manager-roster.jsx): Availability, Assignments,
// Coverage, Swaps & cover, By day, By person. Desktop drag-and-drop with a
// non-drag fallback for every move (tap-a-day, swap-in buttons, click-to-confirm).

import { Fragment, useState } from 'react'
import { EvIcon as Icon } from './EvIcon'
import type { LibraryRole, RosterAssignment, VolunteerOffer, VolunteerOfferStatus } from '../../types'
import {
  EV, ROLE_TONES, toneOf, toneForRole, EvAvatar, EvBadge, EvCard, StatusDot, ProgressBar,
  SectionHead, EvEmpty, primaryBtn, ghostBtn, fmtDayChip, wdLong, wdShort,
  dayNum, moShort, firstName
} from './evKit'

// ---- shared drag payload (mirrored module-level so dragover can read it) ----
interface DragPayload { userId: number; assignmentId: number | null }
let dragData: DragPayload | null = null

export interface VolunteerLite {
  id: number
  name: string | null
  initials: string | null
  canDo: number[]
  availDays: string[]
  phone?: string | null
  offerStatus?: VolunteerOfferStatus | null
}

export interface RosterCtx {
  eventId: number
  roles: LibraryRole[]
  days: string[]
  assignments: RosterAssignment[]
  offers: VolunteerOffer[]
  byCell: (roleId: number, date: string) => RosterAssignment[]
  forVol: (userId: number) => RosterAssignment[]
  vol: (userId: number) => VolunteerLite
  role: (roleId: number) => LibraryRole | undefined
  pool: VolunteerLite[]
  involved: VolunteerLite[]
  signedOn: (userId: number) => boolean
  isAdmin: boolean
  archived: boolean
  actions: {
    assign: (userId: number, roleId: number, date: string) => void
    move: (assignmentId: number, roleId: number, date: string) => void
    unassign: (assignmentId: number) => void
    confirmChip: (assignmentId: number) => void
    confirmVolunteer: (userId: number) => void
    confirmAllAssigned: () => void
    confirmRole: (roleId: number) => void
    reviewOffer: (offerId: number, decision: 'approved' | 'declined') => void
  }
}

function needOf(role: LibraryRole | undefined): number | null {
  return role?.needPerDay ?? null
}
function isEligible(vol: VolunteerLite, roleId: number): boolean {
  return vol.canDo.length === 0 ? true : vol.canDo.includes(roleId)
}

// ---- PersonChip (draggable) ------------------------------------------------
function PersonChip({ vol, status, assignmentId, draggable = true, onRemove, compact, live, writable = true }: {
  vol: VolunteerLite
  status?: 'assigned' | 'confirmed'
  assignmentId?: number | null
  draggable?: boolean
  onRemove?: () => void
  compact?: boolean
  live?: boolean
  writable?: boolean
}) {
  const [hover, setHover] = useState(false)
  const canDrag = draggable && writable
  return (
    <div
      draggable={canDrag}
      onDragStart={(e) => { dragData = { userId: vol.id, assignmentId: assignmentId ?? null }; e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', String(vol.id)) }}
      onDragEnd={() => { dragData = null }}
      onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#fff', border: `1px solid ${EV.line}`, borderRadius: 999, padding: compact ? '2px 4px 2px 3px' : '3px 7px 3px 4px', cursor: canDrag ? 'grab' : 'default', maxWidth: '100%', position: 'relative', boxShadow: hover && canDrag ? '0 2px 8px rgba(0,0,0,0.1)' : 'none' }}>
      {live ? <span title="On duty now" style={{ width: 9, height: 9, borderRadius: '50%', background: EV.live, boxShadow: '0 0 0 2px #fff', animation: 'el-pulse 1.8s infinite', flexShrink: 0 }} /> : (status && <StatusDot status={status} />)}
      <EvAvatar name={vol.name} size={compact ? 20 : 24} tone="green" />
      <span style={{ fontFamily: EV.body, fontSize: compact ? 11.5 : 12.5, fontWeight: 700, color: EV.ink, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{compact ? firstName(vol.name) : vol.name}</span>
      {onRemove && writable && hover && <button onClick={onRemove} style={{ width: 18, height: 18, borderRadius: '50%', border: 'none', background: EV.oxbloodSoft, color: EV.oxblood, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', marginLeft: 1 }}><Icon name="x" size={11} color={EV.oxblood} strokeWidth={2.6} /></button>}
    </div>
  )
}

function Legend({ dot, text }: { dot: React.ReactNode; text: string }) {
  return <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>{dot} {text}</span>
}

function StatCard({ value, label, tone = 'slate', foot, big }: { value: string; label: string; tone?: keyof typeof ROLE_TONES; foot?: React.ReactNode; big?: boolean }) {
  const t = ROLE_TONES[tone] || ROLE_TONES.slate
  return (
    <div style={{ flex: 1, minWidth: 150, background: '#fff', border: `1px solid ${EV.line}`, borderRadius: 10, padding: '14px 16px' }}>
      <div style={{ fontFamily: EV.serif, fontSize: big ? 30 : 22, fontWeight: 800, color: t.fg, lineHeight: 1 }}>{value}</div>
      <div style={{ fontFamily: EV.body, fontSize: 11.5, fontWeight: 700, color: EV.inkSoft, textTransform: 'uppercase', letterSpacing: 0.5, marginTop: 5 }}>{label}</div>
      {foot && <div style={{ marginTop: 9 }}>{foot}</div>}
    </div>
  )
}

function roleTimes(role: LibraryRole): string {
  const a = role.defaultShiftStart, b = role.defaultShiftEnd
  if (!a && !b) return ''
  return `${a ?? '?'}–${b ?? '?'}`
}

// ===========================================================================
// 1) AVAILABILITY — the InfoHub inbox
// ===========================================================================
export function AvailabilityView({ ctx }: { ctx: RosterCtx }) {
  const pending = ctx.offers.filter((o) => o.status === 'pending')
  const processed = ctx.offers.filter((o) => o.status !== 'pending')
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 920 }}>
      <SectionHead title="Incoming from InfoHub" count={pending.length} sub="Volunteers who put themselves forward. Approve to accept them, then place them into roles below or on the Assignments grid." />
      {pending.length === 0 && <EvEmpty icon="bell" text="No new offers right now. Use “Send call for volunteers” to push the banner to InfoHub." />}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {pending.map((o) => <AvailabilityCard key={o.id} offer={o} ctx={ctx} />)}
      </div>
      {processed.length > 0 && (
        <>
          <SectionHead title="Reviewed" count={processed.length} sub="Already approved or declined." />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {processed.map((o) => {
              const myDuties = ctx.forVol(o.userId)
              const hasAssigned = myDuties.some((a) => a.status === 'assigned')
              const allConfirmed = myDuties.length > 0 && !hasAssigned
              const writable = ctx.isAdmin && !ctx.archived
              return (
                <div key={o.id} style={{ display: 'flex', alignItems: 'center', gap: 12, background: '#fff', border: `1px solid ${hasAssigned ? '#e3d6a8' : EV.line}`, borderRadius: 9, padding: '9px 12px', opacity: o.status === 'declined' ? 0.6 : 1 }}>
                  <EvAvatar name={o.volunteer.name} size={30} />
                  <span style={{ fontFamily: EV.body, fontWeight: 700, fontSize: 13 }}>{o.volunteer.name}</span>
                  <span style={{ fontFamily: EV.body, fontSize: 12, color: EV.inkSoft }}>{o.mode === 'whole' ? `All ${ctx.days.length} days` : o.days.map(fmtDayChip).join(', ')}</span>
                  <span style={{ flex: 1 }} />
                  {o.status === 'approved' && hasAssigned && <span style={{ fontFamily: EV.body, fontSize: 10.5, fontWeight: 800, color: '#9b7f2a', background: EV.brassSoft, border: '1px solid #e3d6a8', borderRadius: 999, padding: '2px 9px', textTransform: 'uppercase', letterSpacing: 0.4 }}>Not confirmed</span>}
                  {allConfirmed
                    ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontFamily: EV.body, fontSize: 11.5, fontWeight: 800, color: EV.live }}><Icon name="check" size={13} color={EV.live} strokeWidth={2.6} /> Confirmed</span>
                    : <EvBadge tone={o.status === 'approved' ? 'green' : 'amber'} solid>{o.status}</EvBadge>}
                  {writable && hasAssigned && <button onClick={() => ctx.actions.confirmVolunteer(o.userId)} style={primaryBtn()}><Icon name="check" size={14} color="#fff" strokeWidth={2.4} /> Confirm &amp; notify</button>}
                  {writable && <button onClick={() => ctx.actions.reviewOffer(o.id, 'approved')} style={ghostBtn()}>Reopen</button>}
                </div>
              )
            })}
          </div>
        </>
      )}
    </div>
  )
}

function AvailabilityCard({ offer, ctx }: { offer: VolunteerOffer; ctx: RosterCtx }) {
  const [open, setOpen] = useState(false)
  const v = ctx.vol(offer.userId)
  const offerDays = offer.mode === 'whole' ? ctx.days : offer.days
  const placed = ctx.forVol(offer.userId)
  const writable = ctx.isAdmin && !ctx.archived
  return (
    <div style={{ background: '#fff', border: `1px solid ${EV.line}`, borderRadius: 11, overflow: 'hidden' }}>
      <div style={{ padding: '13px 15px', display: 'flex', gap: 12, alignItems: 'flex-start' }}>
        <PersonChip vol={v} compact writable={writable} />
        <div style={{ flex: 1, minWidth: 0, marginLeft: 2 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontFamily: EV.serif, fontSize: 16, fontWeight: 700, color: EV.ink }}>{offer.volunteer.name}</span>
            <span style={{ fontFamily: EV.body, fontSize: 11.5, color: EV.inkSoft }}>· {offer.volunteer.priorEvents} events{offer.volunteer.sinceYear ? ` since ${offer.volunteer.sinceYear}` : ''}</span>
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 7, alignItems: 'center' }}>
            <span style={{ fontFamily: EV.mono, fontSize: 10, fontWeight: 700, color: EV.inkSoft, textTransform: 'uppercase', letterSpacing: 0.4 }}>Offered</span>
            {offerDays.map((d) => <EvBadge key={d} tone="slate">{fmtDayChip(d)}</EvBadge>)}
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6, alignItems: 'center' }}>
            <span style={{ fontFamily: EV.mono, fontSize: 10, fontWeight: 700, color: EV.inkSoft, textTransform: 'uppercase', letterSpacing: 0.4 }}>Roles</span>
            {offer.roleIds.map((rid) => { const r = ctx.role(rid); return <EvBadge key={rid} tone={r ? toneKeyOf(r) : 'slate'}>{r?.name ?? `#${rid}`}</EvBadge> })}
          </div>
          {offer.note && <div style={{ marginTop: 8, fontFamily: EV.body, fontSize: 12.5, fontStyle: 'italic', color: EV.inkSoft, background: EV.parchment, borderRadius: 8, padding: '7px 10px' }}>“{offer.note}”</div>}
          {offer.readConfirmed && <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 6, fontFamily: EV.body, fontSize: 11.5, color: EV.live, fontWeight: 700 }}><Icon name="check" size={13} color={EV.live} strokeWidth={2.6} /> Confirmed they've read the role duties</div>}
        </div>
        {writable && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 7, flexShrink: 0 }}>
            <button onClick={() => ctx.actions.reviewOffer(offer.id, 'approved')} style={primaryBtn()}><Icon name="check" size={14} color="#fff" strokeWidth={2.4} /> Approve</button>
            <button onClick={() => ctx.actions.reviewOffer(offer.id, 'declined')} style={ghostBtn()}>Decline</button>
            <button onClick={() => setOpen((o) => !o)} style={{ ...ghostBtn(), color: EV.green }}>{open ? 'Hide' : 'Place'} <Icon name="chevronDown" size={13} color={EV.green} style={{ transform: open ? 'rotate(180deg)' : 'none' }} /></button>
          </div>
        )}
      </div>
      {open && writable && <QuickPlace offer={offer} offerDays={offerDays} ctx={ctx} placed={placed} />}
    </div>
  )
}

function QuickPlace({ offer, offerDays, ctx, placed }: { offer: VolunteerOffer; offerDays: string[]; ctx: RosterCtx; placed: RosterAssignment[] }) {
  return (
    <div style={{ borderTop: `1px solid ${EV.line}`, background: EV.parchment, padding: '12px 15px' }}>
      <div style={{ fontFamily: EV.body, fontSize: 11.5, fontWeight: 800, color: EV.inkSoft, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>Place into a role — tap a day</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {offer.roleIds.map((rid) => {
          const role = ctx.role(rid)
          if (!role) return null
          const need = needOf(role)
          return (
            <div key={rid} style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <span style={{ minWidth: 150, fontFamily: EV.body, fontSize: 13, fontWeight: 700, color: EV.ink, display: 'flex', alignItems: 'center', gap: 7 }}><span style={{ width: 9, height: 9, borderRadius: '50%', background: toneOf(role).dot }} />{role.name}</span>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {offerDays.map((d) => {
                  const cell = ctx.byCell(rid, d)
                  const isPlaced = cell.some((a) => a.userId === offer.userId)
                  const full = need != null && cell.length >= need
                  return (
                    <button key={d} onClick={() => isPlaced ? undefined : ctx.actions.assign(offer.userId, rid, d)} disabled={isPlaced}
                      style={{ padding: '6px 10px', borderRadius: 8, border: `1px solid ${isPlaced ? EV.live : full ? EV.line : EV.green}`, background: isPlaced ? '#dcebe5' : '#fff', color: isPlaced ? '#0a4a3a' : full ? EV.inkSoft : EV.green, fontFamily: EV.body, fontSize: 12, fontWeight: 700, cursor: isPlaced ? 'default' : 'pointer', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                      {isPlaced ? <Icon name="check" size={12} color="#0a4a3a" strokeWidth={2.6} /> : <Icon name="plus" size={12} color={full ? EV.inkSoft : EV.green} strokeWidth={2.4} />}
                      {fmtDayChip(d)}{!isPlaced && need != null && ` · ${cell.length}/${need}`}
                    </button>
                  )
                })}
              </div>
            </div>
          )
        })}
      </div>
      {placed.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 12, paddingTop: 11, borderTop: `1px solid ${EV.line}` }}>
          <span style={{ fontFamily: EV.body, fontSize: 12.5, color: EV.inkSoft }}>Placed in <strong style={{ color: EV.ink }}>{placed.length}</strong> {placed.length === 1 ? 'duty' : 'duties'}.</span>
          <span style={{ flex: 1 }} />
          {placed.some((a) => a.status === 'assigned')
            ? <button onClick={() => ctx.actions.confirmVolunteer(offer.userId)} style={primaryBtn()}><Icon name="check" size={14} color="#fff" strokeWidth={2.4} /> Confirm all &amp; notify</button>
            : <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontFamily: EV.body, fontSize: 12.5, fontWeight: 800, color: EV.live }}><Icon name="check" size={14} color={EV.live} strokeWidth={2.6} /> Confirmed — can sign in</span>}
        </div>
      )}
    </div>
  )
}

// ===========================================================================
// 2) ASSIGNMENTS — role × day grid with drag/drop + a placeable pool
// ===========================================================================
export function AssignmentsView({ ctx }: { ctx: RosterCtx }) {
  const [over, setOver] = useState<string | null>(null)
  const writable = ctx.isAdmin && !ctx.archived

  // Drop handler. Default = ADD the person to the dropped role, so one person
  // can hold several roles on the same day (item 2). Hold Shift while dropping
  // an already-placed chip to MOVE (relocate) it instead. Dropping someone onto
  // a cell they're already in is a no-op — we never duplicate a person within
  // one role/day.
  const onDrop = (roleId: number, date: string, moveInstead: boolean) => {
    if (!dragData || !writable) return
    const { userId, assignmentId } = dragData
    const alreadyHere = ctx.byCell(roleId, date).some((a) => a.userId === userId && a.id !== assignmentId)
    if (!alreadyHere) {
      if (assignmentId && moveInstead) ctx.actions.move(assignmentId, roleId, date)
      else ctx.actions.assign(userId, roleId, date)
    }
    setOver(null); dragData = null
  }

  return (
    <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
      {/* pool rail */}
      <div style={{ width: 210, flexShrink: 0, position: 'sticky', top: 0 }}>
        <EvCard>
          <div style={{ padding: '11px 13px', borderBottom: `1px solid ${EV.line}` }}>
            <div style={{ fontFamily: EV.serif, fontSize: 14, fontWeight: 700 }}>Available to place</div>
            <div style={{ fontFamily: EV.body, fontSize: 11, color: EV.inkSoft, marginTop: 1 }}>{writable ? 'Drag onto the grid →' : 'People who offered via InfoHub'}</div>
          </div>
          <div className="el-scroll" style={{ maxHeight: 460, overflowY: 'auto', padding: 10, display: 'flex', flexDirection: 'column', gap: 7 }}>
            {ctx.pool.length === 0 && <div style={{ fontFamily: EV.body, fontSize: 12, fontStyle: 'italic', color: EV.inkSoft, padding: '6px 2px' }}>No offers yet.</div>}
            {ctx.pool.map((v) => (
              <div key={v.id} style={{ border: `1px solid ${EV.line}`, borderRadius: 9, padding: '7px 8px', background: v.offerStatus === 'approved' ? '#f3f8f5' : '#fff' }}>
                <PersonChip vol={v} compact writable={writable} />
                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 5 }}>
                  {v.canDo.slice(0, 3).map((rid) => { const r = ctx.role(rid); return <span key={rid} title={r?.name} style={{ width: 8, height: 8, borderRadius: '50%', background: r ? toneOf(r).dot : EV.slate }} /> })}
                  <span style={{ fontFamily: EV.mono, fontSize: 9.5, color: EV.inkSoft, marginLeft: 'auto' }}>{v.availDays.length === ctx.days.length ? `${ctx.days.length}d` : `${v.availDays.length}d`}</span>
                </div>
              </div>
            ))}
          </div>
        </EvCard>
      </div>

      {/* grid — bounded scroll box so the horizontal scrollbar is always
          reachable (it used to sit at the bottom of a very tall grid, off-screen);
          day-header row sticky to the top, role column sticky to the left. */}
      <div style={{ flex: 1, minWidth: 0, overflow: 'auto', maxHeight: 'calc(100vh - 250px)' }} className="el-scroll">
        <div style={{ display: 'grid', gridTemplateColumns: `190px repeat(${ctx.days.length}, minmax(190px, 1fr))`, gap: 8, minWidth: 190 + ctx.days.length * 190 }}>
          <div style={{ position: 'sticky', top: 0, left: 0, zIndex: 3, background: EV.parchment }} />
          {ctx.days.map((d) => (
            <div key={d} style={{ position: 'sticky', top: 0, zIndex: 2, background: '#fff', border: `1px solid ${EV.line}`, borderRadius: 8, padding: '8px 10px', textAlign: 'center', boxShadow: `0 0 0 4px ${EV.parchment}` }}>
              <div style={{ fontFamily: EV.body, fontSize: 10.5, fontWeight: 800, letterSpacing: 0.6, textTransform: 'uppercase', color: EV.inkSoft }}>{wdLong(d)}</div>
              <div style={{ fontFamily: EV.serif, fontSize: 16, fontWeight: 700 }}>{dayNum(d)} {moShort(d)}</div>
            </div>
          ))}
          {ctx.roles.map((role) => (
            <Fragment key={role.id}>
              <div style={{ position: 'sticky', left: 0, zIndex: 1, background: '#fff', border: `1px solid ${EV.line}`, borderRadius: 8, padding: '9px 11px', display: 'flex', flexDirection: 'column', justifyContent: 'center', boxShadow: `0 0 0 4px ${EV.parchment}` }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}><span style={{ width: 9, height: 9, borderRadius: '50%', background: toneOf(role).dot, flexShrink: 0 }} /><span style={{ fontFamily: EV.body, fontSize: 13, fontWeight: 800, color: EV.ink }}>{role.name}</span></div>
                <div style={{ fontFamily: EV.mono, fontSize: 10.5, color: EV.inkSoft, marginTop: 3 }}>{roleTimes(role)}{needOf(role) != null ? `${roleTimes(role) ? ' · ' : ''}${needOf(role)}/day` : ''}</div>
                {writable && ctx.days.some((d) => ctx.byCell(role.id, d).some((a) => a.status === 'assigned')) && (
                  <button
                    onClick={() => ctx.actions.confirmRole(role.id)}
                    title="Confirm & notify everyone placed in this role across all days"
                    style={{ marginTop: 7, display: 'inline-flex', alignItems: 'center', gap: 5, alignSelf: 'flex-start', background: EV.greenSoft, color: EV.green, border: `1px solid ${EV.green}`, borderRadius: 8, padding: '4px 9px', fontFamily: EV.body, fontSize: 11, fontWeight: 800, cursor: 'pointer' }}
                  >
                    <Icon name="check" size={12} color={EV.green} strokeWidth={2.6} /> Approve all
                  </button>
                )}
              </div>
              {ctx.days.map((date) => {
                const cell = ctx.byCell(role.id, date)
                const key = `${role.id}|${date}`
                const need = needOf(role)
                const full = need != null && cell.length >= need
                const elig = !!dragData && isEligible(ctx.vol(dragData.userId), role.id)
                const isOver = over === key
                return (
                  <div key={key}
                    onDragOver={(e) => { if (!writable) return; e.preventDefault(); if (over !== key) setOver(key) }}
                    onDragLeave={() => setOver((o) => o === key ? null : o)}
                    onDrop={(e) => { e.preventDefault(); onDrop(role.id, date, e.shiftKey) }}
                    style={{ minHeight: 64, borderRadius: 8, border: isOver ? `2px dashed ${elig ? EV.green : EV.amber}` : `1px solid ${EV.line}`, background: isOver ? (elig ? '#eef6f1' : '#fdf3e8') : full ? '#f7faf8' : '#fff', padding: 7, display: 'flex', flexDirection: 'column', gap: 5, transition: 'background .1s' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span style={{ fontFamily: EV.mono, fontSize: 10, fontWeight: 700, color: full ? EV.live : cell.length === 0 ? EV.oxblood : EV.amber }}>{cell.length}{need != null ? `/${need}` : ''}</span>
                      {full && <Icon name="check" size={12} color={EV.live} strokeWidth={2.6} />}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      {cell.map((a) => (
                        <div key={a.id} onClick={() => writable && a.status === 'assigned' ? ctx.actions.confirmChip(a.id) : undefined} title={a.status === 'assigned' ? 'Click to confirm' : 'Confirmed'} style={{ cursor: writable && a.status === 'assigned' ? 'pointer' : 'grab' }}>
                          <PersonChip vol={ctx.vol(a.userId)} status={a.status} assignmentId={a.id} compact live={ctx.signedOn(a.userId)} onRemove={() => ctx.actions.unassign(a.id)} writable={writable} />
                        </div>
                      ))}
                      {cell.length === 0 && !isOver && <span style={{ fontFamily: EV.body, fontSize: 11, fontStyle: 'italic', color: '#bba', textAlign: 'center', padding: '8px 0' }}>{writable ? 'drop here' : '—'}</span>}
                    </div>
                  </div>
                )
              })}
            </Fragment>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 16, marginTop: 12, fontFamily: EV.body, fontSize: 11.5, color: EV.inkSoft, flexWrap: 'wrap' }}>
          <Legend dot={<StatusDot status="assigned" />} text="Assigned — click chip to confirm" />
          <Legend dot={<StatusDot status="confirmed" />} text="Confirmed — volunteer can sign in" />
          <Legend dot={<span style={{ fontFamily: EV.mono, fontWeight: 700, color: EV.oxblood }}>0/n</span>} text="Gap to fill" />
        </div>
      </div>
    </div>
  )
}

// ===========================================================================
// 3) COVERAGE — gaps at a glance
// ===========================================================================
export function CoverageView({ ctx }: { ctx: RosterCtx }) {
  const perCell = (rid: number, d: string) => { const need = needOf(ctx.role(rid)) ?? 0; const got = ctx.byCell(rid, d).length; return { need, got, gap: Math.max(0, need - got) } }
  let totalNeed = 0, totalGot = 0
  ctx.roles.forEach((r) => ctx.days.forEach((d) => { const c = perCell(r.id, d); totalNeed += c.need; totalGot += Math.min(c.got, c.need) }))
  const totalGap = totalNeed - totalGot
  const pendingOffers = ctx.offers.filter((o) => o.status === 'pending').length
  return (
    <div style={{ maxWidth: 980 }}>
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <StatCard big value={`${totalGot}/${totalNeed}`} label="Slots filled" tone="green" foot={<ProgressBar filled={totalGot} need={totalNeed} height={7} />} />
        <StatCard big value={String(totalGap)} label="Slots still open" tone={totalGap ? 'amber' : 'green'} />
        <StatCard big value={String(pendingOffers)} label="Offers awaiting review" tone="slate" />
      </div>
      <SectionHead title="Heat map" sub="Each cell shows filled / needed. Red = empty, amber = short, green = covered." />
      {totalNeed === 0 && <div style={{ marginTop: 10 }}><EvEmpty icon="info" text="No per-day targets set yet. Use “Configure roles” to set how many of each role you need per day." /></div>}
      <div style={{ overflowX: 'auto', marginTop: 10 }} className="el-scroll">
        <div style={{ display: 'grid', gridTemplateColumns: `180px repeat(${ctx.days.length}, 1fr)`, gap: 6, minWidth: 520 }}>
          <div />
          {ctx.days.map((d) => <div key={d} style={{ textAlign: 'center', fontFamily: EV.body, fontSize: 12, fontWeight: 800, color: EV.inkSoft, padding: '4px 0' }}>{fmtDayChip(d)}</div>)}
          {ctx.roles.map((r) => (
            <Fragment key={r.id}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 7, fontFamily: EV.body, fontSize: 12.5, fontWeight: 700 }}><span style={{ width: 9, height: 9, borderRadius: '50%', background: toneOf(r).dot }} />{r.name}</div>
              {ctx.days.map((d) => {
                const c = perCell(r.id, d)
                const col = c.need === 0 ? { bg: EV.parchment, fg: EV.inkSoft } : c.got === 0 ? { bg: '#fbe9e9', fg: EV.oxblood } : c.gap > 0 ? { bg: '#faeadb', fg: '#9a5a1e' } : { bg: '#dcebe5', fg: '#0a4a3a' }
                return <div key={d} style={{ background: col.bg, borderRadius: 7, padding: '10px 6px', textAlign: 'center', fontFamily: EV.mono, fontSize: 13, fontWeight: 700, color: col.fg }}>{c.got}{c.need ? `/${c.need}` : ''}{c.gap > 0 && <div style={{ fontFamily: EV.body, fontSize: 9.5, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.4, marginTop: 1 }}>−{c.gap}</div>}</div>
              })}
            </Fragment>
          ))}
        </div>
      </div>
    </div>
  )
}

// ===========================================================================
// 4) SWAPS & COVER
// ===========================================================================
export function SwapsView({ ctx }: { ctx: RosterCtx }) {
  const [flags, setFlags] = useState<Record<number, string>>({})
  const writable = ctx.isAdmin && !ctx.archived
  const flagged = Object.keys(flags).map((id) => ctx.assignments.find((a) => a.id === Number(id))).filter(Boolean) as RosterAssignment[]
  const others = ctx.assignments.filter((a) => !flags[a.id])

  const resolveWith = (a: RosterAssignment, newVolId: number) => {
    ctx.actions.unassign(a.id)
    ctx.actions.assign(newVolId, a.roleId, a.assignmentDate)
    setFlags((f) => { const n = { ...f }; delete n[a.id]; return n })
  }

  return (
    <div style={{ maxWidth: 900 }}>
      <SectionHead title="Needs cover" count={flagged.length} sub="Drop-outs and cancellations. Pick a suggested replacement — only eligible, available volunteers are shown." />
      {flagged.length === 0 && <EvEmpty icon="swap" text="Nothing needs cover. Flag any assignment below as a drop-out to arrange cover." />}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 4 }}>
        {flagged.map((a) => {
          const v = ctx.vol(a.userId); const role = ctx.role(a.roleId)
          const cands = ctx.pool.filter((c) => c.id !== v.id && isEligible(c, a.roleId)
            && !ctx.byCell(a.roleId, a.assignmentDate).some((x) => x.userId === c.id)
            && !ctx.forVol(c.id).some((x) => x.assignmentDate === a.assignmentDate))
          return (
            <div key={a.id} style={{ background: '#fff', border: `1px solid ${EV.oxblood}`, borderRadius: 11, overflow: 'hidden', boxShadow: '0 0 0 3px rgba(122,35,41,0.06)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 15px', background: '#fdf2f2' }}>
                <span style={{ width: 36, height: 36, borderRadius: '50%', background: EV.oxbloodSoft, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon name="alert" size={18} color={EV.oxblood} /></span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontFamily: EV.body, fontSize: 14, fontWeight: 800 }}>{v.name} — {flags[a.id]}</div>
                  <div style={{ fontFamily: EV.body, fontSize: 12, color: EV.inkSoft }}>{role?.name} · {fmtDayChip(a.assignmentDate)}{roleTimes(role!) ? ` · ${roleTimes(role!)}` : ''}</div>
                </div>
                <button onClick={() => setFlags((f) => { const n = { ...f }; delete n[a.id]; return n })} style={ghostBtn()}>Keep them on</button>
              </div>
              <div style={{ padding: '11px 15px' }}>
                <div style={{ fontFamily: EV.body, fontSize: 11.5, fontWeight: 800, color: EV.inkSoft, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>Suggested cover ({cands.length})</div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {cands.map((c) => (
                    <button key={c.id} onClick={() => resolveWith(a, c.id)} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, border: `1px solid ${EV.green}`, background: '#fff', borderRadius: 999, padding: '5px 12px 5px 5px', cursor: 'pointer' }}>
                      <EvAvatar name={c.name} size={26} /><span style={{ fontFamily: EV.body, fontSize: 12.5, fontWeight: 700, color: EV.ink }}>{c.name}</span><span style={{ fontFamily: EV.body, fontSize: 11, color: EV.green, fontWeight: 800 }}>Swap in</span>
                    </button>
                  ))}
                  {cands.length === 0 && <span style={{ fontFamily: EV.body, fontSize: 12.5, fontStyle: 'italic', color: EV.inkSoft }}>No eligible volunteers free that day — check the Availability tab or send another call.</span>}
                </div>
              </div>
            </div>
          )
        })}
      </div>

      <SectionHead title="All assignments" sub="Flag anyone who's dropped out or needs covering." style={{ marginTop: 18 }} />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: 8, marginTop: 4 }}>
        {[...others].sort((a, b) => a.assignmentDate.localeCompare(b.assignmentDate)).map((a) => { const v = ctx.vol(a.userId); const role = ctx.role(a.roleId); return (
          <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 9, background: '#fff', border: `1px solid ${EV.line}`, borderRadius: 9, padding: '8px 10px' }}>
            <StatusDot status={a.status} /><EvAvatar name={v.name} size={28} />
            <div style={{ flex: 1, minWidth: 0 }}><div style={{ fontFamily: EV.body, fontSize: 12.5, fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{v.name}</div><div style={{ fontFamily: EV.body, fontSize: 10.5, color: EV.inkSoft }}>{role?.name} · {fmtDayChip(a.assignmentDate)}</div></div>
            {writable && <button onClick={() => setFlags((f) => ({ ...f, [a.id]: 'Drop-out' }))} title="Flag as drop-out" style={{ width: 28, height: 28, borderRadius: 7, border: `1px solid ${EV.line}`, background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: EV.oxblood }}><Icon name="swap" size={14} color={EV.oxblood} /></button>}
          </div>
        ) })}
      </div>
    </div>
  )
}

// ===========================================================================
// 5) BY DAY
// ===========================================================================
export function ByDayView({ ctx }: { ctx: RosterCtx }) {
  const [day, setDay] = useState(ctx.days[0] ?? '')
  const [over, setOver] = useState<number | null>(null)
  const writable = ctx.isAdmin && !ctx.archived
  // Default = add to the dropped role (item 2); Shift-drop relocates an
  // already-placed chip; dropping onto a cell they're already in is a no-op.
  const onDrop = (roleId: number, moveInstead: boolean) => { if (!dragData || !writable) return; const { userId, assignmentId } = dragData; const alreadyHere = ctx.byCell(roleId, day).some((a) => a.userId === userId && a.id !== assignmentId); if (!alreadyHere) { if (assignmentId && moveInstead) ctx.actions.move(assignmentId, roleId, day); else ctx.actions.assign(userId, roleId, day) } setOver(null); dragData = null }
  const totalNeed = ctx.roles.reduce((n, r) => n + (needOf(r) ?? 0), 0)
  return (
    <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', maxWidth: 1000 }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
          {ctx.days.map((d) => { const on = d === day; const filled = ctx.assignments.filter((a) => a.assignmentDate === d).length; return (
            <button key={d} onClick={() => setDay(d)} style={{ flex: 1, minWidth: 120, borderRadius: 10, padding: '12px 10px', border: `1.5px solid ${on ? EV.green : EV.line}`, background: on ? '#fff' : '#faf8f3', cursor: 'pointer', textAlign: 'left', boxShadow: on ? '0 2px 8px rgba(10,74,58,0.12)' : 'none' }}>
              <div style={{ fontFamily: EV.body, fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.6, color: on ? EV.green : EV.inkSoft }}>{wdLong(d)}</div>
              <div style={{ fontFamily: EV.serif, fontSize: 19, fontWeight: 800, color: EV.ink }}>{dayNum(d)} {moShort(d)}</div>
              {totalNeed > 0 && <><div style={{ marginTop: 6 }}><ProgressBar filled={Math.min(filled, totalNeed)} need={totalNeed} /></div><div style={{ fontFamily: EV.mono, fontSize: 10.5, color: EV.inkSoft, marginTop: 4 }}>{Math.min(filled, totalNeed)}/{totalNeed} slots</div></>}
            </button>
          ) })}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
          {ctx.roles.map((role) => {
            const cell = ctx.byCell(role.id, day); const need = needOf(role); const full = need != null && cell.length >= need; const key = role.id
            const elig = !!dragData && isEligible(ctx.vol(dragData.userId), role.id)
            return (
              <div key={role.id} onDragOver={(e) => { if (!writable) return; e.preventDefault(); if (over !== key) setOver(key) }} onDragLeave={() => setOver((o) => o === key ? null : o)} onDrop={(e) => { e.preventDefault(); onDrop(role.id, e.shiftKey) }}
                style={{ background: '#fff', border: over === key ? `2px dashed ${elig ? EV.green : EV.amber}` : `1px solid ${EV.line}`, borderRadius: 10, padding: '11px 14px', display: 'flex', alignItems: 'center', gap: 14 }}>
                <div style={{ width: 170, flexShrink: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}><span style={{ width: 9, height: 9, borderRadius: '50%', background: toneOf(role).dot }} /><span style={{ fontFamily: EV.body, fontSize: 13.5, fontWeight: 800 }}>{role.name}</span></div>
                  <div style={{ fontFamily: EV.mono, fontSize: 10.5, color: EV.inkSoft, marginTop: 2 }}>{roleTimes(role)}</div>
                </div>
                <div style={{ flex: 1, display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                  {cell.map((a) => <div key={a.id} onClick={() => writable && a.status === 'assigned' && ctx.actions.confirmChip(a.id)} style={{ cursor: writable && a.status === 'assigned' ? 'pointer' : 'grab' }}><PersonChip vol={ctx.vol(a.userId)} status={a.status} assignmentId={a.id} live={ctx.signedOn(a.userId)} onRemove={() => ctx.actions.unassign(a.id)} writable={writable} /></div>)}
                  {need != null && Array.from({ length: Math.max(0, need - cell.length) }).map((_, i) => <span key={i} style={{ fontFamily: EV.body, fontSize: 11.5, fontStyle: 'italic', color: '#bba', border: `1px dashed ${EV.line}`, borderRadius: 999, padding: '5px 12px' }}>open</span>)}
                </div>
                <span style={{ fontFamily: EV.mono, fontSize: 12, fontWeight: 700, color: full ? EV.live : cell.length === 0 ? EV.oxblood : EV.amber, flexShrink: 0 }}>{cell.length}{need != null ? `/${need}` : ''}</span>
              </div>
            )
          })}
        </div>
      </div>
      <div style={{ width: 200, flexShrink: 0 }}>
        <EvCard>
          <div style={{ padding: '10px 12px', borderBottom: `1px solid ${EV.line}`, fontFamily: EV.serif, fontSize: 13.5, fontWeight: 700 }}>Free this day</div>
          <div className="el-scroll" style={{ maxHeight: 420, overflowY: 'auto', padding: 9, display: 'flex', flexDirection: 'column', gap: 6 }}>
            {ctx.pool.filter((v) => (v.availDays.length === 0 || v.availDays.includes(day)) && !ctx.forVol(v.id).some((a) => a.assignmentDate === day)).map((v) => <PersonChip key={v.id} vol={v} compact writable={writable} />)}
          </div>
        </EvCard>
      </div>
    </div>
  )
}

// ===========================================================================
// 6) BY PERSON
// ===========================================================================
export function ByPersonView({ ctx }: { ctx: RosterCtx }) {
  const involved = ctx.involved
  const [sel, setSel] = useState<number | null>(involved[0]?.id ?? null)
  const writable = ctx.isAdmin && !ctx.archived
  if (involved.length === 0) return <EvEmpty icon="user" text="No volunteers involved yet. Approve offers on the Availability tab to get started." />
  const selId = sel ?? involved[0]!.id
  const v = ctx.vol(selId)
  const myA = ctx.forVol(selId)
  const offer = ctx.offers.find((o) => o.userId === selId)
  return (
    <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', maxWidth: 1000 }}>
      <div style={{ width: 240, flexShrink: 0 }}>
        <EvCard>
          <div className="el-scroll" style={{ maxHeight: 540, overflowY: 'auto' }}>
            {involved.map((p) => { const on = p.id === selId; const conf = ctx.forVol(p.id).filter((a) => a.status === 'confirmed').length; const tot = ctx.forVol(p.id).length; return (
              <button key={p.id} onClick={() => setSel(p.id)} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', border: 'none', borderLeft: on ? `3px solid ${EV.green}` : '3px solid transparent', background: on ? EV.parchment : '#fff', cursor: 'pointer', textAlign: 'left', borderBottom: `1px solid ${EV.line}` }}>
                <EvAvatar name={p.name} size={32} />
                <div style={{ flex: 1, minWidth: 0 }}><div style={{ fontFamily: EV.body, fontSize: 13, fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.name}</div><div style={{ fontFamily: EV.body, fontSize: 10.5, color: EV.inkSoft }}>{tot ? `${conf}/${tot} confirmed` : 'Awaiting placement'}</div></div>
              </button>
            ) })}
          </div>
        </EvCard>
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <EvCard style={{ padding: 18 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <EvAvatar name={v.name} size={52} />
            <div style={{ flex: 1 }}>
              <div style={{ fontFamily: EV.serif, fontSize: 21, fontWeight: 700 }}>{v.name}</div>
              <div style={{ fontFamily: EV.body, fontSize: 12.5, color: EV.inkSoft }}>{offer ? `${offer.volunteer.priorEvents} events${offer.volunteer.sinceYear ? ` since ${offer.volunteer.sinceYear}` : ''}` : ''}{v.phone ? ` · ${v.phone}` : ''}</div>
            </div>
            {writable && myA.some((a) => a.status === 'assigned') && <button onClick={() => ctx.actions.confirmVolunteer(selId)} style={primaryBtn()}><Icon name="check" size={14} color="#fff" strokeWidth={2.4} /> Confirm all</button>}
          </div>
          {v.canDo.length > 0 && (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 12 }}>
              <span style={{ fontFamily: EV.mono, fontSize: 10, fontWeight: 700, color: EV.inkSoft, textTransform: 'uppercase', letterSpacing: 0.4, alignSelf: 'center' }}>Can do</span>
              {v.canDo.map((rid) => { const r = ctx.role(rid); return <EvBadge key={rid} tone={r ? toneKeyOf(r) : 'slate'}>{r?.name ?? `#${rid}`}</EvBadge> })}
            </div>
          )}
        </EvCard>

        <SectionHead title="Across the event" sub="Confirmed and pending duties, plus open days they offered." style={{ marginTop: 16 }} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 4 }}>
          {ctx.days.map((d) => {
            const dayA = myA.filter((a) => a.assignmentDate === d)
            const offered = offer && (offer.mode === 'whole' || offer.days.includes(d))
            return (
              <div key={d} style={{ display: 'flex', alignItems: 'center', gap: 12, background: '#fff', border: `1px solid ${EV.line}`, borderRadius: 9, padding: '11px 14px' }}>
                <div style={{ width: 100, flexShrink: 0 }}><div style={{ fontFamily: EV.body, fontSize: 11, fontWeight: 800, textTransform: 'uppercase', color: EV.inkSoft }}>{wdShort(d)}</div><div style={{ fontFamily: EV.serif, fontSize: 16, fontWeight: 700 }}>{dayNum(d)} {moShort(d)}</div></div>
                <div style={{ flex: 1, display: 'flex', gap: 7, flexWrap: 'wrap', alignItems: 'center' }}>
                  {dayA.map((a) => { const r = ctx.role(a.roleId); const t = r ? toneOf(r) : ROLE_TONES.slate; return <span key={a.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 7, background: t.bg, borderRadius: 8, padding: '5px 10px' }}><StatusDot status={a.status} /><span style={{ fontFamily: EV.body, fontSize: 12.5, fontWeight: 700, color: t.fg }}>{r?.name}</span>{roleTimes(r!) && <span style={{ fontFamily: EV.mono, fontSize: 10.5, color: t.fg, opacity: 0.8 }}>{roleTimes(r!)}</span>}</span> })}
                  {dayA.length === 0 && <span style={{ fontFamily: EV.body, fontSize: 12.5, fontStyle: 'italic', color: offered ? EV.green : '#bba' }}>{offered ? 'Offered — not yet placed' : 'Not available / not offered'}</span>}
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

// helper: tone key for a role (badge tone prop wants the key, not the object)
function toneKeyOf(role: LibraryRole) { return toneForRole(role) }
